import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoveUpOutlinedIcon from '@mui/icons-material/MoveUpOutlined';
import {
  Chip, IconButton, TablePagination, Tooltip,
} from '@mui/material';
import Checkbox, { checkboxClasses } from '@mui/material/Checkbox';
import LinearProgress from '@mui/material/LinearProgress';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import deleteSwatchColours from '../../../../api/oc_product/productDataManagement/colourMatching/delete/deleteSwatchColours';
import postMoveSwatchColour from '../../../../api/oc_product/productDataManagement/colourMatching/post/postMoveSwatchColour';
import DeleteConfirationDialog from './DeleteConfirmationDialog';
import MoveColoursModal from './MoveColoursModal';

function ColourTable({
  language,
  rowsData,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  searchValue,
  colourType,
  getSwatchColourDetails,
  swatchColoursName,
  translatedSwatchColoursName,
  langName,
  loading,
}) {
  const [editMode, setEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedColours, setSelectedColours] = useState([]);
  const [colourGroupName, setColourGroupName] = useState(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  const [selectedNewColourGroupName, setSelectedNewColourGroupName] = React.useState('');

  const handleDeleteColours = async () => {
    try {
      if (selectedIds.length === 0) {
        toast.error('please select colours');
        return;
      }
      await deleteSwatchColours(colourGroupName, language, selectedIds.join(', '));
      const coloursName = (selectedColours.join(', '));
      toast.success(`Colours: ${coloursName} deleted`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete colours');
    } finally {
      setOpenDeleteModal(false);
      getSwatchColourDetails(language, searchValue, colourType);
    }
  };

  const handleMoveColours = async () => {
    try {
      if (selectedIds.length === 0) {
        toast.error('please select colours');
        return;
      }
      await postMoveSwatchColour(language, selectedNewColourGroupName, selectedIds.join(', '));
      const coloursName = (selectedColours.length > 0) ? selectedColours.join(', ') : '';
      toast.success(`Colours: ${coloursName} moved from ${colourGroupName} to ${selectedNewColourGroupName}`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete colours');
    } finally {
      setOpenModal(false);
      getSwatchColourDetails(language, searchValue, colourType);
    }
  };

  const startEditColourGroup = (groupName) => {
    setEditMode((prev) => !prev);
    setColourGroupName(groupName);
    setSelectedIds([]);
    setSelectedColours([]);
  };

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
    setSelectedIds([]);
    setSelectedColours([]);
  };

  const toggleSelect = (id, name) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setSelectedColours((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]));
  };

  const handleMove = () => {
    if (!selectedIds.length) return;
    setEditMode(false);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setOpenDeleteModal(false);
    setSelectedIds([]);
    setSelectedColours([]);
  };

  const handleDelete = () => {
    if (!selectedIds.length) return;
    setEditMode(false);
    setOpenDeleteModal(true);
  };

  const groupedRowsData = React.useMemo(() => {
    if (language === 1) {
      return rowsData.map((g) => ({
        groupName: g.SwatchColour1,
        Colours: g.Colours,
      }));
    }
    // for non english colours
    const grouped = rowsData.reduce((acc, item) => {
      const key = item.TranslatedColour;

      if (!acc[key]) {
        // eslint-disable-next-line no-param-reassign
        acc[key] = {
          groupName: key,
          Colours: [],
        };
      }
      acc[key].Colours.push({
        Id: item.Id,
        UkColourId: item.UkColourId,
        Colour: item.Colour,
        TranslatedColour: item.TranslatedColour,
      });

      return acc;
    }, {});
    return Object.values(grouped);
  }, [rowsData]);

  const paginatedData = groupedRowsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return (
    <div className="flex flex-col items-center w-full p-8 mx-auto bg-white shadow-lg">
      <div className="min-w-full divide-y divide-gray-200">
        {paginatedData && paginatedData.length > 0 ? (
          paginatedData.map((group) => (
            <div
              key={group.groupName}
              className="p-6 mb-4 rounded-lg shadow-md bg-slate-50"
            >
              <div className="inline-block float-right -mt-6">
                {!editMode ? (
                  <Tooltip title="Move/Delete Colours">
                    <IconButton
                      size="large"
                      onClick={() => startEditColourGroup(group.groupName)}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  group.groupName === colourGroupName
                  && (
                    <>
                      <Tooltip title="Move colours">
                        <IconButton
                          size="large"
                          color="secondary"
                          disabled={!selectedIds.length}
                          onClick={handleMove}
                        >
                          <MoveUpOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete colours">
                        <IconButton
                          size="large"
                          color="error"
                          disabled={!selectedIds.length}
                          onClick={handleDelete}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="large"
                          onClick={toggleEditMode}
                        >
                          <ClearOutlinedIcon />
                        </IconButton>
                      </Tooltip>

                    </>
                  )
                )}
              </div>
              <div className="flex mb-2 space-x-3">
                <h3 className="text-xl font-bold text-gray-950">
                  {group.groupName}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2 overflow-y-auto custom-scrollbar max-h-52">
                {group.Colours
                  && group.Colours.map((colour) => (
                    <div
                      key={language === 1 ? colour.SwatchColourId : colour.UkColourId}
                      className="flex items-center text-gray-900 rounded-md"
                    >
                      <Chip
                        disabled={editMode && group.groupName !== colourGroupName}
                        label={colour.Colour}
                        color="secondary"
                        size="large"
                        // variant="outlined"
                        clickable={editMode && group.groupName === colourGroupName}
                        onClick={() => toggleSelect(language === 1
                          ? String(colour.SwatchColourId)
                          : String(colour.Id), String(colour.Colour))}
                        sx={{ bgcolor: '#586b80', color: '#fff' }}
                        icon={(
                          editMode && group.groupName === colourGroupName && (
                            <Checkbox
                              checked={selectedIds.includes(
                                language === 1 ? String(colour.SwatchColourId) : String(colour.Id),
                              )}
                              onChange={() => toggleSelect(
                                language === 1 ? String(colour.SwatchColourId) : String(colour.Id),
                                String(colour.Colour),
                              )}
                              onClick={() => toggleSelect(language === 1
                                ? String(colour.SwatchColourId)
                                : String(colour.Id), String(colour.Colour))}
                              size="small"
                              sx={{
                                padding: 0,
                                backgroundColor: '#586b80',
                                [`&, &.${checkboxClasses.checked}`]: {
                                  color: 'white',
                                },
                              }}
                            />
                          )
                        )}
                      />
                    </div>
                  ))}
              </div>
            </div>
          ))
        ) : (
          <div className="px-6 py-4 text-center">
            {loading && paginatedData && !paginatedData.length ? <LinearProgress color="inherit" /> : 'No Colours Available'}
          </div>
        )}
      </div>
      <DeleteConfirationDialog
        open={openDeleteModal}
        handleClose={handleModalClose}
        selectedColours={selectedColours}
        colourGroupName={colourGroupName}
        // deleteColours={deleteColours}
        handleDeleteColours={handleDeleteColours}
      />
      <MoveColoursModal
        open={openModal}
        handleClose={handleModalClose}
        selectedColours={selectedColours}
        colourGroupName={colourGroupName}
        handleMoveColours={handleMoveColours}
        selectedNewColourGroupName={selectedNewColourGroupName}
        setSelectedNewColourGroupName={setSelectedNewColourGroupName}
        swatchColoursName={swatchColoursName}
        translatedSwatchColoursName={translatedSwatchColoursName}
        languageId={language}
        langName={langName}
      />
      <TablePagination
        component="div"
        count={groupedRowsData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        labelRowsPerPage="Rows per page:"
      />
    </div>
  );
}

export default ColourTable;
